import { SPEC_FILE_NAME, STAGED_FILES_SNAPSHOT_ID } from "@/constants";
import { useFilesystemStore, useMessageStore } from "@/state/stores";
import { useMemo } from "react";

function generateFileLinkProps(
  projectId: string | undefined,
  path: string,
  snapshotId: string | null,
  messageIsLoading: boolean,
) {
  if (!projectId || !snapshotId) {
    return null;
  }

  if (path === SPEC_FILE_NAME) {
    return {
      to: "/codegen/$projectId/filesystem/$snapshotId" as const,
      params: {
        projectId,
        snapshotId: messageIsLoading ? STAGED_FILES_SNAPSHOT_ID : snapshotId,
      },
      search: { tab: "spec" as const },
    };
  }

  return {
    to: "/codegen/$projectId/filesystem/$snapshotId" as const,
    params: {
      projectId,
      snapshotId: messageIsLoading ? STAGED_FILES_SNAPSHOT_ID : snapshotId,
    },
    search: { tab: "code" as const, file: path },
  };
}

export function useFileLinkProps(
  projectId: string | undefined,
  messageId: string,
  maybeSnapshotId: string | null,
  path: string,
) {
  const messageIsLoading = useMessageStore((state) => {
    const thisMessage = state.messages.find((m) => m.id === messageId);
    if (!thisMessage) {
      return false;
    }

    if (thisMessage.status === "processing") {
      return true;
    }

    if (thisMessage.status === "pending") {
      return true;
    }

    return false;
  });

  // If a snapshot id is provided, use it. Otherwise, use the filesystem store to find the snapshot id,
  // based on the message id.
  // If we still don't find it then, fall back to the staged files snapshot id.
  // This is probably error prone.
  const snapshotId = useFilesystemStore((state) => {
    if (maybeSnapshotId) {
      return maybeSnapshotId;
    }

    const matchByMessage = state.snapshots.find(
      (s) => s.label === `message:${messageId}`,
    )?.id;

    if (matchByMessage) {
      return matchByMessage;
    }

    return STAGED_FILES_SNAPSHOT_ID;
  });

  return useMemo(() => {
    return generateFileLinkProps(
      projectId,
      path || "",
      snapshotId,
      messageIsLoading,
    );
  }, [projectId, path, snapshotId, messageIsLoading]);
}
